import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Invitation } from '@/api/users';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { onDateTime } from '@/pages/PersonCard/cardDates';
import { type } from '@/theme/typography';

interface Props {
  /** Whose link this is — for the sentence, never for the URL (the token names nobody). */
  name: string;
  invitation: Invitation;
  /** A first link (after a create) reads differently from a replacement. */
  reissued?: boolean;
  onClose: () => void;
}

/**
 * The one-time invitation link, shown **once** (§6.10, backend TASK-100).
 *
 * ⚠️ **This dialog is the only place in the product a live credential is ever on screen**, and the
 * copy is written for that: it says the link is shown once, that a lost one is replaced rather than
 * looked up, and that *nobody* can retrieve it — support included. That is not a warning for
 * politeness; the token is stored as a SHA-256, so it is literally true, and a reader who assumes
 * otherwise closes this dialog and blocks a colleague until somebody works out that reissuing is the
 * answer.
 *
 * ⚠️ **The URL is composed here, from `window.location.origin` plus the server's relative `path`.**
 * The server deliberately does not build it: a tenant may have several branded hosts and the API has
 * no way to know which one the Admin is looking at, so a full URL from the server could hand somebody
 * a link to a domain they cannot reach. The origin the Admin is *already on* is the one that works.
 *
 * ⚠️ **Copying is offered and the link is also selectable.** `navigator.clipboard` needs a secure
 * context and a permission; when it is unavailable — an http:// dev host is the common case — the
 * failure is a sentence telling the reader to select it by hand, not a silent no-op on a button that
 * looked like it worked.
 */
export function InvitationDialog({ name, invitation, reissued = false, onClose }: Props) {
  const { t, i18n } = useTranslation('settings');
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const url = `${window.location.origin}${invitation.path}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t('invitation.title')}
      /*
       * ⚠️ **The sentence naming whose link this is, as the dialog's `description`** (BUG-046). It used
       * to be the first `<p>` of the body, which left `DialogContent` with no description at all: Radix
       * warned on every open (*"Missing `Description` or `aria-describedby={undefined}`"*) and a screen
       * reader announced the title alone. That matters more here than on any other dialog in the
       * product — this is the only place a live credential is on screen, and the reader has to know
       * whose it is and that it is shown once. `Dialog` renders this as a real `RadixDialog.Description`
       * (BUG-022's shape: the association is programmatic, not visual proximity).
       */
      description={
        reissued ? t('invitation.reissued', { name }) : t('invitation.created', { name })
      }
      footer={<Button onClick={onClose}>{t('invitation.done')}</Button>}
    >
      <div className="space-y-3">
        {/* The link itself: selectable, wrapping, and monospaced so a reader can check what they
            pasted. `break-all` because a token has no spaces to break at. */}
        {/*
          ⚠️ **`border-rule` is correct here, and the class order is not accidental.** This is a
          paragraph rather than a control, so WCAG 1.4.11's 3:1 boundary does not apply. But
          `theme.test.ts` sweeps each line for a control name followed by that hairline — to catch a
          decorative border on a real control — and the selectability utility on this element read to
          it as one of those names. It sits on its own line so the guard keeps catching what it is
          for; a weakened regex there would be the real loss. (The same rule caught this comment when
          it quoted both tokens on one line, which is why it no longer does.)
        */}
        <p
          className={cn(
            type.numeral,
            'select-all',
            'break-all rounded border border-rule bg-paper-sunken p-3 text-xs',
          )}
          data-testid="invitation-link"
        >
          {url}
        </p>

        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={copy}>
            {copied ? (
              <Check size={16} strokeWidth={1.5} aria-hidden />
            ) : (
              <Copy size={16} strokeWidth={1.5} aria-hidden />
            )}
            {copied ? t('invitation.copied') : t('invitation.copy')}
          </Button>
          <span className={type.metadata}>
            {t('invitation.expires', { date: onDateTime(invitation.expires_at, i18n.language) })}
          </span>
        </div>

        {copyFailed && <p className="text-xs text-negative">{t('invitation.copyFailed')}</p>}

        {/* ⚠️ The standing warning, and it is the reason this dialog has a footer button rather than
            only an `Esc`: the reader has to acknowledge something before the link is gone. */}
        <p className={cn(type.metadata, 'not-italic')}>{t('invitation.warning')}</p>
      </div>
    </Dialog>
  );
}
